import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

const CATEGORIES_BASE = '/flights/ssr-categories';
const ITEMS_BASE = '/flights/ssr-items';

const CATEGORY_COLOR_MAP = {
  SEAT: '#3b82f6',
  MEAL: '#10b981',
  HANDBAG: '#8b5cf6',
  BAGGAGE: '#f59e0b',
  ASSIST: '#ef4444',
  SPORT: '#06b6d4',
  PET: '#ec4899',
  PERSONAL: '#a855f7',
  OTHER: '#6b7280',
};

/**
 * Map API category to UI format
 */
export const mapCategoryApiToUi = (api) => {
  if (!api) return null;
  return {
    id: api.id,
    code: api.code ?? '',
    name: api.name ?? '',
    icon: api.icon ?? '📋',
    description: api.description ?? '',
    color: CATEGORY_COLOR_MAP[api.code] ?? '#6b7280',
  };
};

/**
 * Map API SSR item to UI format
 */
export const mapItemApiToUi = (api) => {
  if (!api) return null;
  return {
    id: api.id,
    code: api.code ?? '',
    description: api.description ?? '',
    category: api.categoryCode ?? '',
    ssrCategoryId: api.ssrCategoryId,
    maxWeight: api.maxWeight != null ? parseFloat(api.maxWeight) : null,
    maxSize: api.maxSize ?? null,
    maxPieces: api.maxPieces != null ? parseInt(api.maxPieces, 10) : null,
    flightSegmentRequired: api.flightSegmentAssignmentRequired ?? false,
    travelerRequired: api.travelerAssignmentRequired ?? false,
    direction: api.direction ?? 'Both',
    status: api.active ? 'active' : 'inactive',
  };
};

/**
 * Map UI item to API format for create/update
 * @param {Object} ui - UI item (has category code or ssrCategoryId)
 * @param {Array} categories - List of categories to resolve category code to id
 */
export const mapItemUiToApi = (ui, categories = []) => {
  let ssrCategoryId = ui.ssrCategoryId ?? ui.categoryId;
  if (!ssrCategoryId && ui.category) {
    const cat = categories.find((c) => c.code === ui.category);
    ssrCategoryId = cat?.id;
  }
  return {
    ssrCategoryId,
    code: String(ui.code ?? '').toUpperCase(),
    description: String(ui.description ?? ''),
    maxWeight: ui.maxWeight != null ? Number(ui.maxWeight) : null,
    maxSize: ui.maxSize || null,
    maxPieces: ui.maxPieces != null ? Number(ui.maxPieces) : null,
    direction: ui.direction ?? 'Both',
    flightSegmentAssignmentRequired: ui.flightSegmentRequired ?? false,
    travelerAssignmentRequired: ui.travelerRequired ?? false,
    active: ui.status === 'active' || ui.active === true,
  };
};

/**
 * useGetSSRCategories - fetch all SSR categories
 */
export const useGetSSRCategories = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(CATEGORIES_BASE);
      const res = response.data;
      if (res.success) {
        setData((res.data || []).map(mapCategoryApiToUi));
      } else {
        setError(res.message || 'Failed to load SSR categories');
        setData([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load SSR categories');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return { data, loading, error, refetch: fetchCategories };
};

/**
 * useGetSSRItems - fetch SSR items with filters
 */
export const useGetSSRItems = (params = {}) => {
  const {
    page = 1,
    limit = 100,
    search = '',
    categoryId = '',
    status = '',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { page, limit };
      if (search) queryParams.search = search;
      if (categoryId) queryParams.category = categoryId;
      if (status && status !== 'all') {
        queryParams.status = status === 'active' ? 'Active' : 'Inactive';
      }

      const response = await axiosInstance.get(ITEMS_BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        setData((res.data || []).map(mapItemApiToUi));
        if (res.pagination) setPagination(res.pagination);
      } else {
        setError(res.message || 'Failed to load SSR items');
        setData([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load SSR items');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, categoryId, status]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { data, pagination, loading, error, refetch: fetchItems };
};

/**
 * useCreateSSRItem, useUpdateSSRItem, useDeleteSSRItem
 */
export const useCreateSSRItem = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (payload, categories = []) => {
    setLoading(true);
    try {
      const apiPayload = mapItemUiToApi(payload, categories);
      const response = await axiosInstance.post(ITEMS_BASE, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        return { success: true, data: mapItemApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to create SSR item' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to create SSR item' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

export const useUpdateSSRItem = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (id, payload, categories = []) => {
    setLoading(true);
    try {
      const apiPayload = mapItemUiToApi(payload, categories);
      const response = await axiosInstance.put(`${ITEMS_BASE}/${id}`, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        return { success: true, data: mapItemApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to update SSR item' };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to update SSR item' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};

export const useDeleteSSRItem = () => {
  const [loading, setLoading] = useState(false);
  const execute = useCallback(async (id) => {
    setLoading(true);
    try {
      await axiosInstance.delete(`${ITEMS_BASE}/${id}`);
      return { success: true };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || 'Failed to delete SSR item' };
    } finally {
      setLoading(false);
    }
  }, []);
  return { execute, loading };
};
